import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  MessageCircleQuestion,
  SkipForward
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type FC,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent
} from "react";

import { Button } from "@/components/ui/button";
import {
  Questionnaire,
  QuestionnaireActions,
  QuestionnaireChoice,
  QuestionnaireChoiceDescription,
  QuestionnaireChoices,
  QuestionnaireDescription,
  QuestionnaireError,
  QuestionnaireInput,
  QuestionnaireItem,
  QuestionnaireNext,
  QuestionnairePrevious,
  QuestionnaireProgress,
  QuestionnaireSkip,
  QuestionnaireSubmit,
  QuestionnaireTitle
} from "@/components/ui/questionnaire";
import { cn } from "@/lib/utils";

import {
  answersFromFormData,
  describeAnswer,
  isSkippedAnswer,
  questionsFromToolInput,
  withSkips
} from "../../plan/model";
import type { AskUserAnswers, AskUserOutput } from "../../types";

import type { AnyToolPart } from "./ToolCard";

type Props = {
  part: AnyToolPart;
  onAnswer: (toolCallId: string, answers: AskUserAnswers) => void;
  /**
   * Rendered as a tab sitting on the composer (rounded top, no bottom edge)
   * rather than as a card in the transcript.
   */
  docked?: boolean;
};

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
/** Long enough to see the pick land before the next question slides in. */
export const ADVANCE_MS = 180;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

const outputOf = (part: AnyToolPart): AskUserOutput | null => {
  if (part.state !== "output-available" || !isRecord(part.output)) return null;
  const { answers } = part.output;
  return {
    answers: isRecord(answers) ? (answers as AskUserAnswers) : null,
    skipped: Boolean(part.output.skipped),
    auto: Boolean(part.output.auto)
  };
};

/**
 * The primitive hides Previous/Next with `hidden` + `inert` when they do not
 * apply. In the header they are the stepper chevrons and should stay put,
 * dimmed, so the layout does not shift between questions.
 */
const alwaysVisible = (
  props: Record<string, unknown>,
  state: { visible: boolean }
) => {
  const {
    hidden: _hidden,
    inert: _inert,
    "aria-hidden": _ariaHidden,
    ...rest
  } = props;
  const button = rest as ComponentProps<"button">;
  return (
    <button
      {...button}
      type="button"
      disabled={Boolean(button.disabled) || !state.visible}
      tabIndex={state.visible ? button.tabIndex : -1}
    />
  );
};

const renderProgress = (
  props: Record<string, unknown>,
  state: { current: number; total: number }
) => (
  <div {...(props as ComponentProps<"div">)}>
    {state.current} of {state.total}
  </div>
);

/** A dimmed keyboard hint inside a button; excluded from its accessible name. */
const KeyHint: FC<{ children: string }> = ({ children }) => (
  <span aria-hidden="true" className="text-xs leading-none opacity-60">
    {children}
  </span>
);

/** Escape skips the current question when the primitive offers Skip. */
const skipOnEscape = (e: KeyboardEvent<HTMLFormElement>) => {
  if (e.key !== "Escape") return;
  const skip = e.currentTarget.querySelector(
    "[data-slot=questionnaire-skip][data-visible]"
  );
  if (skip instanceof HTMLButtonElement && !skip.disabled) {
    e.preventDefault();
    skip.click();
  }
};

/**
 * The agent's `ask_user` question as a shadcn `Questionnaire`: one item per
 * question (single or multiple choice, with an optional freeform "Other…"
 * answer), stepped one question at a time. Any question can be skipped,
 * whatever its `required`: Skip (or Escape) moves on and sends the card from
 * the last question; Skip All skips the current question and every later
 * one. Submitting writes the answers as the tool part's output, `null` for
 * each skipped question; once the part has output the recorded answers
 * render read-only in its place.
 */
export const QuestionCard: FC<Props> = ({ part, onAnswer, docked }) => {
  const { title, questions } = useMemo(
    () => questionsFromToolInput(part.input),
    [part.input]
  );
  const output = outputOf(part);
  const items = useMemo(
    () =>
      questions.map((q) => ({
        name: q.name,
        choices: q.choices.map((c) => ({ value: c.value }))
      })),
    [questions]
  );

  const [item, setItem] = useState<string | undefined>(questions[0]?.name);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    []
  );

  if (!questions.length) return null;

  if (output) {
    const only = questions.length === 1 ? questions[0] : null;
    if (only && !output.skipped && output.answers) {
      const answer = output.answers[only.name];
      const skipped = isSkippedAnswer(answer);
      return (
        <div
          className="flex items-center gap-1.5 rounded-lg bg-elevated px-3 py-2 text-base inset-ring inset-ring-line-2"
          data-testid="question-card-answered"
          title={only.prompt}
        >
          {skipped ? (
            <SkipForward className="size-3.5 shrink-0 text-fg-3" />
          ) : (
            <CheckCircle2 className="size-3.5 shrink-0 text-success" />
          )}
          <span className="truncate">
            {skipped ? (
              <>Skipped: {only.prompt}</>
            ) : (
              <>
                {output.auto ? "Went with Recommended" : "Answered"}:{" "}
                {describeAnswer(only, answer)}
              </>
            )}
          </span>
        </div>
      );
    }
    const noneGiven =
      !output.skipped &&
      Boolean(output.answers) &&
      questions.every((q) => isSkippedAnswer(output.answers?.[q.name]));
    return (
      <div
        className="space-y-1.5 rounded-lg bg-elevated px-3 py-2 text-base inset-ring inset-ring-line-2"
        data-testid="question-card-answered"
      >
        <div className="flex items-center gap-1.5 font-medium">
          {noneGiven ? (
            <SkipForward className="size-3.5 text-fg-3" />
          ) : (
            <CheckCircle2 className="size-3.5 text-success" />
          )}
          {output.skipped || !output.answers
            ? "Answered in the chat"
            : output.auto
            ? "Went With Recommended Answers"
            : title ?? "Your answers"}
        </div>
        {!output.skipped && output.answers ? (
          <dl className="space-y-1.5">
            {questions.map((q) => (
              <div key={q.name} className="grid gap-0.5">
                <dt className="text-sm text-fg-3">{q.prompt}</dt>
                <dd
                  className={cn(
                    isSkippedAnswer(output.answers?.[q.name]) && "text-fg-3"
                  )}
                >
                  {describeAnswer(q, output.answers?.[q.name])}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-fg-3">
            You replied in the chat instead of picking here, so the assistant
            took that message as your answer.
          </p>
        )}
      </div>
    );
  }

  const busy = part.state === "input-streaming";

  /** A single-choice pick answers its question: move on to the next one. */
  const advanceOnPick = (e: FormEvent<HTMLFormElement>) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== "radio") return;
    const at = questions.findIndex((q) => q.name === input.name);
    const next = at >= 0 ? questions[at + 1] : undefined;
    if (!next) return;
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => {
      advanceTimer.current = null;
      setItem(next.name);
    }, ADVANCE_MS);
  };

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const answers = answersFromFormData(
      new FormData(e.currentTarget),
      questions
    );
    onAnswer(part.toolCallId, withSkips(answers, questions));
  };

  const at = Math.max(
    0,
    questions.findIndex((q) => q.name === item)
  );
  const canSkipAll = questions.length > 1 && at < questions.length - 1;

  /**
   * Skip the current question and every later one, and send the answers
   * before it. Not the form's submit: that stops at a question not reached.
   */
  const skipAll = (e: MouseEvent<HTMLButtonElement>) => {
    const { form } = e.currentTarget;
    if (!form) return;
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    const answers = answersFromFormData(
      new FormData(form),
      questions.slice(0, at)
    );
    onAnswer(part.toolCallId, withSkips(answers, questions));
  };

  return (
    <div
      className={cn(
        "text-base",
        docked
          ? "mx-3 rounded-t-xl border border-b-0 border-accent-line bg-elevated px-3 pt-3 pb-2.5"
          : "rounded-xl bg-elevated p-3 inset-ring inset-ring-accent-line",
        busy && "opacity-70"
      )}
      data-testid="question-card"
      data-docked={docked ? "" : undefined}
    >
      <Questionnaire
        items={items}
        item={item}
        onItemChange={setItem}
        shortcuts="letters"
        onSubmit={submit}
        onChange={advanceOnPick}
        onKeyDown={skipOnEscape}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1.5 text-fg-3">
            <MessageCircleQuestion className="size-3.5 shrink-0" />
            <span className="truncate">{title ?? "Questions"}</span>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <QuestionnairePrevious
              variant="ghost"
              size="icon-xs"
              aria-label="Previous question"
              className="size-5 text-fg-3 disabled:opacity-35"
              render={alwaysVisible}
            >
              <ChevronUp className="size-3" />
            </QuestionnairePrevious>
            <QuestionnaireProgress render={renderProgress} />
            <QuestionnaireNext
              variant="ghost"
              size="icon-xs"
              aria-label="Next question"
              className="size-5 text-fg-3 disabled:opacity-35"
              render={alwaysVisible}
            >
              <ChevronDown className="size-3" />
            </QuestionnaireNext>
          </div>
        </div>
        {questions.map((q, i) => (
          <QuestionnaireItem key={q.name} name={q.name} multiple={q.multiple}>
            {/*
              The number hangs in the same 16px column as the choice badges,
              so the prompt, its description and the choice labels share one
              left edge. A fieldset's legend sits outside the flex gap, so the
              spacing below it is an explicit margin: 8px to the choices, 4px
              to a description.
            */}
            <QuestionnaireTitle className="mb-2 flex gap-2.5 px-1.5">
              <span className="w-4 shrink-0">{i + 1}.</span>
              <span className="min-w-0 flex-1">{q.prompt}</span>
            </QuestionnaireTitle>
            {q.description ? (
              <QuestionnaireDescription className="-mt-1 pr-1.5 pl-8">
                {q.description}
              </QuestionnaireDescription>
            ) : null}
            {q.choices.length ? (
              <QuestionnaireChoices>
                {q.choices.map((c) => (
                  <QuestionnaireChoice key={c.value} value={c.value}>
                    <span>
                      {c.label}
                      {c.recommended ? (
                        <span
                          className="ml-1.5 inline-block rounded-xs bg-accent-soft px-1 align-middle text-xs font-medium text-primary"
                          data-testid="question-choice-recommended"
                        >
                          Recommended
                        </span>
                      ) : null}
                    </span>
                    {c.description ? (
                      <QuestionnaireChoiceDescription>
                        {c.description}
                      </QuestionnaireChoiceDescription>
                    ) : null}
                  </QuestionnaireChoice>
                ))}
              </QuestionnaireChoices>
            ) : null}
            {q.kind === "date" ? (
              <QuestionnaireInput
                type="date"
                aria-label={q.choices.length ? "Pick a date instead" : "Date"}
                data-testid="question-date-input"
                className="w-auto flex-none [color-scheme:light] dark:[color-scheme:dark]"
                shortcut={
                  q.choices.length ? LETTERS[q.choices.length] : undefined
                }
              />
            ) : q.allow_other !== false ? (
              <QuestionnaireInput
                placeholder={q.choices.length ? "Other…" : "Type your answer…"}
                aria-label={q.choices.length ? "Another answer" : "Your answer"}
                shortcut={
                  q.choices.length ? LETTERS[q.choices.length] : undefined
                }
              />
            ) : null}
            <QuestionnaireError className="pl-8" />
          </QuestionnaireItem>
        ))}
        <QuestionnaireActions className="pt-0.5">
          {canSkipAll ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mr-auto px-2 text-sm text-fg-3"
              disabled={busy}
              onClick={skipAll}
            >
              Skip All
            </Button>
          ) : null}
          <QuestionnaireSkip
            variant="ghost"
            size="sm"
            className="gap-1.5 px-2 text-sm text-fg-3"
          >
            Skip
            <KeyHint>Esc</KeyHint>
          </QuestionnaireSkip>
          <QuestionnaireNext size="sm" className="gap-1.5 px-3 text-sm">
            Continue
            <KeyHint>⏎</KeyHint>
          </QuestionnaireNext>
          <QuestionnaireSubmit size="sm" className="gap-1.5 px-3 text-sm">
            Submit Answers
            <KeyHint>⏎</KeyHint>
          </QuestionnaireSubmit>
        </QuestionnaireActions>
      </Questionnaire>
    </div>
  );
};
